/**
 * AppMenu — global navigation (☰ menu + Home button) for the shared AppHeader (S48).
 *
 * Lives at the far-left of the brand row so every authenticated page gets identical nav.
 * Role-gated: each user sees only items their role can use. "Soon" items render greyed
 * with a pill and are NOT navigable. The Admin submenu deep-links into /admin?section=…
 *
 * No icon library is installed (no new deps), so glyphs use the project's emoji/unicode
 * convention: ☰ (menu), 🏠 (home). The role→route allow-list is the shared single source
 * of truth (canAccessRoute / resolveHomeRoute from @avsuite/shared).
 *
 * Accessibility: ☰ has aria-haspopup/aria-expanded/aria-controls; Esc and click-outside
 * close; focus moves into the panel on open and returns to ☰ on close.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resolveHomeRoute } from '@avsuite/shared';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../lib/useIsMobile';
import StudentClientCreateModal from './admin/StudentClientCreateModal';
import { useUpdates } from './content/UpdatesModal';

// Operational-staff role set (Ops Board access).
const OPS_ROLES = ['ops_controller', 'instructor', 'admin', 'haamc'] as const;
// Base staff roles that may create a student/client (mirrors backend BASE_STAFF_ROLE_NAMES).
// Gates the "New student / client" Admin child so non-admin staff can reach it (Phase 2).
const STAFF_ROLES = ['pilot', 'instructor', 'ops_controller', 'haamc', 'admin'] as const;
// Enquiries access adds the two escalation-recipient add-on roles (mirrors backend
// STAFF_ROLE_NAMES) so a recipient sees the tracker even if it is their only role.
const ENQUIRY_ROLES = [...STAFF_ROLES, 'senior_base_pilot', 'ops_manager'] as const;

type Gate = readonly string[] | 'all';
// A submenu child may carry its OWN role gate (defaults to the submenu's gate) and may be an
// action (opens a modal) rather than a navigation link.
type SubItem = { key: string; label: string; path?: string; soon?: boolean; roles?: Gate; action?: 'new-person' };
// A top-level item may also be an ACTION (opens a panel) rather than a navigation link —
// "Updates" is one: it has no route, so it can never become a home_route (ROUTE_ACCESS_ROLES
// stays the single source of truth for navigable routes and is untouched by it).
type ActionKind = 'updates';
type MenuItem =
  | { kind: 'link'; key: string; label: string; path: string; roles: Gate }
  | { kind: 'action'; key: string; label: string; roles: Gate; action: ActionKind }
  | { kind: 'soon'; key: string; label: string; roles: Gate }
  | { kind: 'submenu'; key: string; label: string; roles: Gate; items: SubItem[] };

// Menu contents, in order (locked with Ben via mockup, 2026-06-17).
const MENU: MenuItem[] = [
  { kind: 'link', key: 'profile', label: 'Profile', path: '/profile', roles: 'all' },
  { kind: 'link', key: 'ops-fs', label: 'Ops Board — Flight School', path: '/ops-board', roles: OPS_ROLES },
  { kind: 'soon', key: 'ops-at', label: 'Ops Board — Air Transport', roles: OPS_ROLES },
  { kind: 'link', key: 'orphaned', label: 'Orphaned bookings', path: '/admin/orphaned', roles: ['admin'] },
  { kind: 'link', key: 'enquiries', label: 'Enquiries', path: '/enquiries', roles: ENQUIRY_ROLES },
  { kind: 'link', key: 'tasks', label: 'Tasks', path: '/tasks', roles: ENQUIRY_ROLES },
  // Label locked with Ben 2026-08-30: "Updates", not "What's New". Permanent entry — the
  // way back to a release note after the popup has been dismissed.
  { kind: 'action', key: 'updates', label: 'Updates', roles: 'all', action: 'updates' },
  { kind: 'soon', key: 'maint', label: 'Maintenance', roles: 'all' },
  { kind: 'soon', key: 'trips', label: 'Trip records', roles: ['pilot', 'instructor', 'admin'] },
  {
    // `roles` here is the DEFAULT child gate; the submenu itself is shown when ANY child is
    // visible (so non-admin staff see Admin → only "New student / client").
    kind: 'submenu', key: 'admin', label: 'Admin', roles: ['admin'],
    items: [
      { key: 'new-person', label: 'New student / client', roles: STAFF_ROLES, action: 'new-person' },
      { key: 'people', label: 'Students & clients', path: '/people', roles: STAFF_ROLES },
      { key: 'users', label: 'Users', path: '/admin?section=users', roles: ['admin'] },
      { key: 'aircraft', label: 'Aircraft', path: '/admin?section=aircraft', roles: ['admin'] },
      { key: 'assets', label: 'Assets', path: '/admin?section=assets', roles: ['admin'] },
      { key: 'bases', label: 'Bases', path: '/admin?section=bases', roles: ['admin'] },
      { key: 'task-settings', label: 'Task reminders', path: '/admin?section=tasks', roles: ['admin'] },
      { key: 'airports', label: 'Airports', soon: true, roles: ['admin'] },
    ],
  },
];

// Single source of truth for visibility — drives rendering.
function canSee(roles: Gate, userRoles: readonly string[]): boolean {
  if (roles === 'all') return true;
  return userRoles.some((r) => (roles as readonly string[]).includes(r));
}

const C = {
  bg: '#1e293b', border: '#334155', text: '#e2e8f0', muted: '#64748b',
  hover: '#334155', active: '#0369a1', activeBg: 'rgba(3,105,161,0.18)',
};

/** Page-supplied action rendered in the ☰ menu on MOBILE only (2026-07-23 header rework) —
 *  lets a page park toolbar buttons (e.g. the Ops Board's Beta/Offline) in the menu on phones. */
export type MenuExtraItem = { key: string; label: string; onSelect: () => void };

type Props = {
  /**
   * Optional: called after the "New student / client" create succeeds, so the hosting page can
   * refresh its people reference (e.g. OpsBoard.loadRefData) — the new person then appears in
   * pickers with no full reload. A no-op on pages that fetch people on mount (the default).
   */
  onPeopleChanged?: () => void;
  /** Mobile-only extra menu actions (ignored on desktop — desktop keeps its toolbar buttons). */
  extraItems?: MenuExtraItem[];
};

export default function AppMenu({ onPeopleChanged, extraItems }: Props) {
  const isMobile = useIsMobile();
  const { user, logout } = useAuth();
  const { openUpdates } = useUpdates();
  const navigate = useNavigate();
  const location = useLocation();
  const userRoles = user?.roles ?? [];

  // Per-item visibility. A submenu child may carry its own gate (defaulting to the submenu's);
  // a submenu is shown when ANY child is visible (so non-admin staff see Admin → just the one item).
  const childVisible = (sub: SubItem, parentRoles: Gate) => canSee(sub.roles ?? parentRoles, userRoles);
  const itemVisible = (it: MenuItem): boolean =>
    it.kind === 'submenu' ? it.items.some((s) => childVisible(s, it.roles)) : canSee(it.roles, userRoles);

  const [open, setOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [showCreatePerson, setShowCreatePerson] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click-outside + Esc; move focus into the panel on open, back to ☰ on close.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const first = panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
    first?.focus();
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }
  async function signOut() {
    setOpen(false);
    await logout();
    navigate('/login', { replace: true });
  }

  function goHome() {
    navigate(resolveHomeRoute(user?.home_route, userRoles));
  }

  // Current-route highlight: match the path portion (ignore query) for links; for an admin
  // submenu item, match /admin + its ?section=.
  const isActiveLink = (path: string) => location.pathname === path;
  const isActiveAdminItem = (path?: string) => {
    if (!path || location.pathname !== '/admin') return false;
    const want = new URLSearchParams(path.split('?')[1] ?? '').get('section');
    const have = new URLSearchParams(location.search).get('section') ?? 'aircraft';
    return want === have;
  };

  const itemBase: CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    width: '100%', textAlign: 'left', background: 'none', border: 'none',
    padding: '9px 14px', fontSize: 13, color: C.text, cursor: 'pointer', fontFamily: 'inherit',
  };
  const soonPill: CSSProperties = {
    fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    color: C.muted, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px',
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="app-global-menu"
        aria-label="Open menu"
        onClick={() => setOpen((v) => !v)}
        style={{ ...iconBtn, fontSize: 18 }}
      >
        ☰
      </button>
      {/* Mobile (2026-07-23 header rework): the standalone Home button moves INTO the ☰ menu
          (first item below) to save brand-row width. Desktop keeps it here, unchanged. */}
      {!isMobile && (
        <button type="button" aria-label="Go to your home page" title="Home" onClick={goHome} style={iconBtn}>
          🏠
        </button>
      )}

      {open && (
        <div
          ref={panelRef}
          id="app-global-menu"
          role="menu"
          aria-label="Global navigation"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 1200,
            minWidth: 248, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.45)', padding: '6px 0', overflow: 'hidden',
          }}
        >
          {/* Mobile only: Home as the first menu item (the standalone 🏠 is hidden on mobile). */}
          {isMobile && (
            <button
              role="menuitem" onClick={() => { setOpen(false); goHome(); }}
              style={itemBase}
              onMouseEnter={(e) => { e.currentTarget.style.background = C.hover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              <span>🏠 Home</span>
            </button>
          )}
          {MENU.filter(itemVisible).map((it) => {
            if (it.kind === 'soon') {
              return (
                <div key={it.key} role="menuitem" aria-disabled="true" style={{ ...itemBase, color: C.muted, cursor: 'default' }}>
                  <span>{it.label}</span><span style={soonPill}>soon</span>
                </div>
              );
            }
            if (it.kind === 'action') {
              return (
                <button
                  key={it.key} role="menuitem"
                  onClick={() => { setOpen(false); openUpdates(); }}
                  style={itemBase}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.hover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <span>{it.label}</span>
                </button>
              );
            }
            if (it.kind === 'link') {
              const active = isActiveLink(it.path);
              return (
                <button
                  key={it.key} role="menuitem" onClick={() => go(it.path)}
                  style={{ ...itemBase, ...(active ? { background: C.activeBg, color: '#7dd3fc', fontWeight: 700 } : {}) }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.hover; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'none'; }}
                >
                  <span>{it.label}</span>{active && <span style={{ fontSize: 11, color: '#7dd3fc' }}>●</span>}
                </button>
              );
            }
            // submenu (Admin)
            return (
              <div key={it.key}>
                <button
                  role="menuitem" aria-haspopup="menu" aria-expanded={adminOpen}
                  onClick={() => setAdminOpen((v) => !v)}
                  style={{ ...itemBase, fontWeight: 600 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.hover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <span>{it.label}</span><span style={{ color: C.muted, fontSize: 11 }}>{adminOpen ? '▾' : '▸'}</span>
                </button>
                {adminOpen && (
                  <div role="menu" aria-label="Admin" style={{ background: 'rgba(0,0,0,0.18)', padding: '2px 0' }}>
                    {it.items.filter((sub) => childVisible(sub, it.roles)).map((sub) => {
                      if (sub.soon) {
                        return (
                          <div key={sub.key} role="menuitem" aria-disabled="true" style={{ ...itemBase, paddingLeft: 28, color: C.muted, cursor: 'default' }}>
                            <span>{sub.label}</span><span style={soonPill}>soon</span>
                          </div>
                        );
                      }
                      if (sub.action === 'new-person') {
                        return (
                          <button
                            key={sub.key} role="menuitem"
                            onClick={() => { setOpen(false); setAdminOpen(false); setShowCreatePerson(true); }}
                            style={{ ...itemBase, paddingLeft: 28 }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = C.hover; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                          >
                            <span>{sub.label}</span>
                          </button>
                        );
                      }
                      const active = isActiveAdminItem(sub.path);
                      return (
                        <button
                          key={sub.key} role="menuitem" onClick={() => go(sub.path!)}
                          style={{ ...itemBase, paddingLeft: 28, ...(active ? { background: C.activeBg, color: '#7dd3fc', fontWeight: 700 } : {}) }}
                          onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.hover; }}
                          onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'none'; }}
                        >
                          <span>{sub.label}</span>{active && <span style={{ fontSize: 11, color: '#7dd3fc' }}>●</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Mobile only: page-supplied toolbar actions parked in the menu (e.g. Ops Board
              Beta/Offline). Desktop pages keep these as toolbar buttons — never rendered there. */}
          {isMobile && extraItems && extraItems.length > 0 && (
            <>
              <div style={{ borderTop: `1px solid ${C.border}`, margin: '6px 0' }} />
              {extraItems.map((x) => (
                <button
                  key={x.key} role="menuitem"
                  onClick={() => { setOpen(false); x.onSelect(); }}
                  style={itemBase}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.hover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <span>{x.label}</span>
                </button>
              ))}
            </>
          )}

          <div style={{ borderTop: `1px solid ${C.border}`, margin: '6px 0 0' }} />
          <button
            role="menuitem" onClick={signOut}
            style={{ ...itemBase, color: '#fca5a5' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.hover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            <span>Sign out</span>
          </button>
        </div>
      )}

      {/* Trimmed staff create flow (Phase 2) — opens over any page; bypasses the admin-only
          entity screens. Reachable by all staff via the "New student / client" Admin item. */}
      {showCreatePerson && (
        <StudentClientCreateModal
          onClose={() => setShowCreatePerson(false)}
          onSaved={() => { setShowCreatePerson(false); onPeopleChanged?.(); }}
        />
      )}
    </div>
  );
}

const iconBtn: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 34, height: 34, background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8,
  color: '#e2e8f0', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0,
};
